import { times } from 'lodash';
import React from 'react';
import { Tooltip } from '../../../Components/Tooltip';
import { colors, Text } from '../../../globalStyles';
import { WidgetContainer } from '../Styles';
import { WidgetProps } from './interface';

export const VisitSatisfaction = ({ data: { visitSatisfactionRatings: ratings } }: WidgetProps) => {
  const ratingAdjusted = ratings.averageRating && Math.round(ratings.averageRating * 10) / 10;

  return (
    <WidgetContainer>
      <div className="flex flex-row items-center justify-between">
        <Text.h2>Visit Satisfaction Rating</Text.h2>
        <Tooltip
          content={`When a patient ends their video appointment, they are asked: "How would you rate your overall visit?" and can select a response on a 1-5 star rating scale. This rating is optional for patients.`}
        />
      </div>
      <Text.h1 className="di mr2">
        {ratingAdjusted ?? '-'}
        <span className="di normal f4 ml2">out of 5</span>
      </Text.h1>
      <div className="mt3 flex flex-row items-center">
        <div className="mr3">
          <Stars ratingRaw={ratingAdjusted} />
        </div>
        <Text.label>
          {ratings.numberOfRatings} {`rating${ratings.numberOfRatings === 1 ? '' : 's'}`}
        </Text.label>
      </div>
    </WidgetContainer>
  );
};

type StarsProps = {
  ratingRaw?: number | null;
  starSize?: number;
  color?: string;
};

const Stars = ({ ratingRaw, starSize = 27, color = colors.brandAlt }: StarsProps) => {
  const rating = ratingRaw ?? 0;
  const filledStars = Math.floor(rating);
  const partialFill = getPartialFill(rating);

  return (
    <svg width={starSize * 5 + 43} viewBox="0 0 200 27" xmlns="http://www.w3.org/2000/svg">
      <mask id="starMask" fill="white">
        <Star fillRule="evenodd" />
      </mask>
      <g fill={color} fillRule="evenodd">
        {/* Show filled stars */}
        {times(filledStars).map(i => (
          <Star key={i} transform={`translate(${43 * i})`} />
        ))}
        {/* Show semi filled stars */}
        {partialFill > 0 && (
          <>
            <Star fill="url(#starGradient)" transform={`translate(${43 * filledStars})`} />
            <StarOutline mask="url(#starMask)" transform={`translate(${43 * filledStars})`} />
          </>
        )}
        {/* Show empty stars */}
        {times(5 - (filledStars + Math.ceil(partialFill))).map(i => (
          <StarOutline
            key={i}
            mask="url(#starMask)"
            transform={`translate(${43 * (i + filledStars + Math.ceil(partialFill))})`}
          />
        ))}
      </g>
      <defs>
        <linearGradient id="starGradient" gradientUnits="objectBoundingBox">
          <stop offset={`${partialFill}`} stopColor={color} stopOpacity="1" />
          <stop offset={`${partialFill}`} stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const Star = (props: Omit<JSX.IntrinsicElements['path'], 'd'>) => (
  <path
    d="M20.4447 24.5431C20.2433 24.5431 20.0419 24.4962 19.8582 24.4L13.3995 21.0256L6.94076 24.4C6.51262 24.6229 5.99582 24.5837 5.60949 24.2999C5.21936 24.0162 5.02556 23.5361 5.10662 23.0611L6.33782 15.9323L1.11916 10.8846C0.770824 10.549 0.646691 10.0448 0.794891 9.58505C0.941824 9.12525 1.33956 8.79085 1.81709 8.72118L9.03836 7.67238L12.2646 1.17945C12.6927 0.318114 14.1063 0.318114 14.5344 1.17945L17.7606 7.67238L24.9819 8.72118C25.4594 8.79085 25.8572 9.12525 26.0041 9.58505C26.1523 10.0448 26.0282 10.549 25.6798 10.8846L20.4612 15.9323L21.6924 23.0611C21.7734 23.5361 21.5796 24.0162 21.1895 24.2999C20.9704 24.462 20.7082 24.5431 20.4447 24.5431Z"
    {...props}
  />
);

const StarOutline = (props: Omit<JSX.IntrinsicElements['path'], 'd'>) => (
  <path
    d="M19.8582 24.4L20.4727 23.2277L20.4711 23.2268L19.8582 24.4ZM13.3995 21.0256L14.0124 19.8524L13.3995 19.5322L12.7866 19.8524L13.3995 21.0256ZM6.94076 24.4L7.55206 25.574L7.55367 25.5731L6.94076 24.4ZM5.60949 24.2999L6.39298 23.2331L6.38799 23.2295L5.60949 24.2999ZM5.10662 23.0611L3.80234 22.8359L3.80189 22.8384L5.10662 23.0611ZM6.33782 15.9323L7.64211 16.1576L7.76126 15.4677L7.25803 14.9809L6.33782 15.9323ZM1.11916 10.8846L2.03937 9.93327L2.03759 9.93156L1.11916 10.8846ZM0.794891 9.58505L2.05467 9.99109L2.05568 9.98794L0.794891 9.58505ZM1.81709 8.72118L1.62685 7.41133L1.62602 7.41145L1.81709 8.72118ZM9.03836 7.67238L9.2286 8.98223L9.91504 8.88254L10.2237 8.26135L9.03836 7.67238ZM12.2646 1.17945L11.0793 0.590308L11.0792 0.590479L12.2646 1.17945ZM14.5344 1.17945L15.7198 0.590479L15.7197 0.590308L14.5344 1.17945ZM17.7606 7.67238L16.5753 8.26135L16.8839 8.88254L17.5704 8.98223L17.7606 7.67238ZM24.9819 8.72118L25.173 7.41145L25.1721 7.41133L24.9819 8.72118ZM26.0041 9.58505L24.7433 9.98794L24.7443 9.99109L26.0041 9.58505ZM25.6798 10.8846L24.7614 9.93155L24.7596 9.93327L25.6798 10.8846ZM20.4612 15.9323L19.541 14.9809L19.0377 15.4677L19.1569 16.1576L20.4612 15.9323ZM21.6924 23.0611L22.9971 22.8384L22.9966 22.8359L21.6924 23.0611ZM21.1895 24.2999L20.411 23.2294L20.4022 23.2359L21.1895 24.2999ZM20.4447 23.2195C20.4486 23.2195 20.4592 23.2206 20.4727 23.2277L19.2438 25.5723C19.6246 25.7719 20.038 25.8667 20.4447 25.8667V23.2195ZM20.4711 23.2268L14.0124 19.8524L12.7866 22.1987L19.2453 25.5731L20.4711 23.2268ZM12.7866 19.8524L6.32785 23.2268L7.55367 25.5731L14.0124 22.1987L12.7866 19.8524ZM6.32946 23.226C6.34604 23.2174 6.37037 23.2165 6.39298 23.2331L4.82601 25.3667C5.62128 25.9508 6.67921 26.0285 7.55206 25.574L6.32946 23.226ZM6.38799 23.2295C6.40718 23.2434 6.41445 23.2657 6.41135 23.2838L3.80189 22.8384C3.63667 23.8066 4.03154 24.7889 4.83099 25.3704L6.38799 23.2295ZM6.41091 23.2864L7.64211 16.1576L5.03354 15.7071L3.80234 22.8359L6.41091 23.2864ZM7.25803 14.9809L2.03936 9.93327L0.198951 11.836L5.41762 16.8837L7.25803 14.9809ZM2.03759 9.93156C2.05679 9.95006 2.05998 9.9746 2.05467 9.99109L-0.464884 9.179C-0.766601 10.1151 -0.515146 11.1479 0.200724 11.8377L2.03759 9.93156ZM2.05568 9.98794C2.04721 10.0144 2.02657 10.0282 2.00817 10.0309L1.62602 7.41145C0.652548 7.55347 -0.16356 8.23605 -0.465894 9.18215L2.05568 9.98794ZM2.00733 10.031L9.2286 8.98223L8.84812 6.36253L1.62685 7.41133L2.00733 10.031ZM10.2237 8.26135L13.4499 1.76842L11.0792 0.590479L7.85302 7.08341L10.2237 8.26135ZM13.4498 1.76859C13.438 1.79239 13.4221 1.81396 13.405 1.83108C13.3886 1.84757 13.3749 1.85613 13.3684 1.85964C13.3623 1.86296 13.3622 1.86201 13.3693 1.8603C13.3763 1.85859 13.3869 1.85704 13.3995 1.85704C13.4121 1.85704 13.4227 1.85859 13.4297 1.8603C13.4368 1.86201 13.4367 1.86296 13.4305 1.85964C13.424 1.85613 13.4104 1.84757 13.3939 1.83108C13.3769 1.81396 13.361 1.79239 13.3492 1.76859L15.7197 0.590308C15.2295 -0.395883 14.2325 -0.790148 13.3995 -0.790148C12.5664 -0.790148 11.5695 -0.395883 11.0793 0.590308L13.4498 1.76859ZM13.3491 1.76842L16.5753 8.26135L18.946 7.08341L15.7198 0.590479L13.3491 1.76842ZM17.5704 8.98223L24.7917 10.031L25.1721 7.41133L17.9509 6.36253L17.5704 8.98223ZM24.7908 10.0309C24.7724 10.0282 24.7518 10.0144 24.7433 9.98794L27.2649 9.18215C26.9625 8.23606 26.1464 7.55347 25.173 7.41145L24.7908 10.0309ZM24.7443 9.99109C24.739 9.9746 24.7422 9.95006 24.7614 9.93156L26.5983 11.8377C27.3141 11.1479 27.5656 10.1151 27.2639 9.179L24.7443 9.99109ZM24.7596 9.93327L19.541 14.9809L21.3814 16.8837L26.6 11.836L24.7596 9.93327ZM19.1569 16.1576L20.3881 23.2864L22.9966 22.8359L21.7654 15.7071L19.1569 16.1576ZM20.3876 23.2838C20.3845 23.2657 20.3918 23.2434 20.411 23.2295L21.968 25.3704C22.7674 24.7889 23.1623 23.8066 22.9971 22.8384L20.3876 23.2838ZM20.4022 23.2359C20.4213 23.2218 20.4378 23.2195 20.4447 23.2195V25.8667C20.9785 25.8667 21.5195 25.7023 21.9767 25.3639L20.4022 23.2359Z"
    {...props}
  />
);

// Set lower and upper bounds for better UI
const getPartialFill = (rating: number) => {
  const decimalVal = rating % 1;
  if (decimalVal > 0 && decimalVal < 0.3) {
    return 0.3;
  }
  if (decimalVal > 0.7) {
    return 0.7;
  }
  return decimalVal;
};
